/**
 * AdCard Component
 * Displays a single ad card with thumbnail on top, name, and production/creative info
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdInList } from '../../types/organization';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useRemoveAdFromList } from '../../hooks/useOrganization';
import './AdCard.css';

interface AdCardProps {
  ad: AdInList;
  listId: string;
  isSelected?: boolean;
  onToggleSelect?: (adId: string) => void;
}

export const AdCard: React.FC<AdCardProps> = ({ ad, listId, isSelected = false, onToggleSelect }) => {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  
  const removeAd = useRemoveAdFromList();

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: ad.ad_id,
    data: {
      type: 'ad',
      ad,
      listId,
    },
  });
  
  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm(`Remove "${ad.ad_name}" from this list?`)) {
      removeAd.mutate({ listId, adId: ad.ad_id });
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getAdPath = (): string => (ad.video_url || '').split('?')[0].split('#')[0];

  const isHtmlAd = (): boolean => {
    const contentType = ad.metadata?.file_metadata?.content_type?.toLowerCase() || '';
    const mediaType = ad.metadata?.media_type?.toLowerCase() || '';
    const path = getAdPath();

    return mediaType === 'html'
      || contentType.startsWith('text/html')
      || /\.(html|htm)$/i.test(path);
  };

  // Check if ad is an image (same logic as AdsTablePage)
  const isImageAd = (): boolean => {
    try {
      const contentType = ad.metadata?.file_metadata?.content_type?.toLowerCase() || '';
      const mediaType = ad.metadata?.media_type?.toLowerCase() || '';
      const path = getAdPath();

      if (isHtmlAd()) return false;
      if (ad.duration === '0' || ad.duration === 0) return true;
      return (
        mediaType === 'image' ||
        contentType.startsWith('image/') ||
        /\.(jpg|jpeg|png|webp)$/i.test(path)
      );
    } catch {
      return false;
    }
  };

  // Generate thumbnail from video (same as AdsTablePage)
  const generateVideoThumbnail = (url: string): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
    try {
      const video = document.createElement('video');
      let resolved = false;
      const cleanup = () => {
        try {
          video.pause();
          video.src = '';
          video.removeAttribute('src');
          video.load();
        } catch {}
      };

      const finishWithCanvas = () => {
        try {
          const width = video.videoWidth || 0;
          const height = video.videoHeight || 0;
          if (!width || !height) { cleanup(); resolve(null); return; }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { cleanup(); resolve(null); return; }
          ctx.drawImage(video, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg');
          cleanup();
          resolve(dataUrl);
        } catch {
          cleanup();
          resolve(null);
        }
      };

      const onSeeked = () => {
        if (resolved) return;
        resolved = true;
        finishWithCanvas();
      };

      video.addEventListener('loadedmetadata', () => {
        try {
          video.currentTime = 0.001;
        } catch {
          finishWithCanvas();
        }
      });

      video.addEventListener('seeked', onSeeked, { once: true });
      video.addEventListener('error', () => { if (!resolved) { resolved = true; cleanup(); resolve(null); } });

      try { video.crossOrigin = 'anonymous'; } catch {}
      video.preload = 'auto';
      video.muted = true;
      video.src = url.includes('#') ? url : `${url}#t=0.001`;
      try { video.load(); } catch {}

      setTimeout(() => { if (!resolved) { resolved = true; cleanup(); resolve(null); } }, 5000);
    } catch {
      resolve(null);
    }
    });
  };

  // Load thumbnail on mount
  useEffect(() => {
    let cancelled = false;

    const loadThumbnail = async () => {
      if (!ad.video_url) {
        setThumbnailError(true);
        return;
      }

      if (isHtmlAd()) {
        setThumbnail(null);
        setThumbnailError(false);
        return;
      }

      // For images, use video_url directly
      if (isImageAd()) {
        setThumbnail(ad.video_url);
        return;
      }

      // For videos, generate thumbnail
      const thumb = await generateVideoThumbnail(ad.video_url);
      if (!cancelled) {
        if (thumb) {
          setThumbnail(thumb);
        } else {
          setThumbnailError(true);
        }
      }
    };

    loadThumbnail();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.video_url]);

  // Check if ad is uploaded (using youtube_video_id and meta_ad_id like AdsTablePage)
  const isUploaded = !!ad.youtube_video_id || !!ad.meta_ad_id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ad-card ${isDragging ? 'dragging' : ''} ${isUploaded ? 'uploaded' : ''} ${isSelected ? 'selected' : ''}`}
      {...attributes}
    >
      {/* Checkbox for selection */}
      {onToggleSelect && (
        <div className="ad-checkbox-wrapper">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(ad.ad_id)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="ad-checkbox"
          />
        </div>
      )}

      {/* Thumbnail - draggable */}
      <div className="ad-thumbnail-compact" {...listeners}>
        {thumbnail && !thumbnailError ? (
          <img 
            src={thumbnail} 
            alt={ad.ad_name} 
            onError={() => setThumbnailError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div className="thumbnail-placeholder">
            <div className="placeholder-icon">{isHtmlAd() ? 'HTML' : '🎬'}</div>
          </div>
        )}
      </div>
      
      {/* Ad info area - draggable except for icons */}
      <div className="ad-card-info">
        <div className="ad-name-row" {...listeners}>
          <h4 className="ad-name" title={ad.ad_id}>
            {ad.ad_id}
          </h4>
        </div>
        
        <div className="ad-metadata">
          <span className="ad-meta-item" {...listeners}>Production:{ad.production_id || '?'}</span>
          <span className="ad-meta-separator" {...listeners}>•</span>
          <span className="ad-meta-item" {...listeners}>Creative:{ad.creative_number || '?'}</span>
          <span className="ad-meta-separator" {...listeners}>•</span>
          {/* Platform icons - NOT draggable */}
          <span className="platform-icons-wrapper">
            <YouTubeIcon
              sx={{
                fontSize: 14,
                color: ad.youtube_video_id ? '#FF0000' : '#666666',
                opacity: ad.youtube_video_id ? 1 : 0.3,
                cursor: ad.youtube_video_id ? 'pointer' : 'default',
              }}
              titleAccess={ad.youtube_video_id ? `Click to copy YouTube ID: ${ad.youtube_video_id}` : 'Not uploaded to YouTube'}
              onClick={(e) => {
                if (ad.youtube_video_id) {
                  e.preventDefault();
                  e.stopPropagation();
                  copyToClipboard(ad.youtube_video_id, 'YouTube ID');
                }
              }}
            />
            <FacebookIcon
              sx={{
                fontSize: 14,
                color: ad.meta_ad_id ? '#1877F2' : '#666666',
                opacity: ad.meta_ad_id ? 1 : 0.3,
                cursor: ad.meta_ad_id ? 'pointer' : 'default',
              }}
              titleAccess={ad.meta_ad_id ? `Click to copy Meta ID: ${ad.meta_ad_id}` : 'Not uploaded to Meta'}
              onClick={(e) => {
                if (ad.meta_ad_id) {
                  e.preventDefault();
                  e.stopPropagation();
                  copyToClipboard(ad.meta_ad_id, 'Meta ID');
                }
              }}
            />
          </span>
          {copySuccess && <span className="copy-success">✓ {copySuccess} copied</span>}
        </div>
      </div>
      
      {/* Action buttons on the right */}
      <div className="ad-card-actions">
        {/* Remove button - removes ad from this list */}
        <button
          className="remove-ad-btn"
          onClick={handleRemove}
          title="Remove from this list"
          disabled={removeAd.isPending}
        >
          ✕
        </button>
        
        {/* Link to ad details */}
        <Link 
          to={ad.ad_page_url} 
          className="view-ad-link"
        >
          →
        </Link>
      </div>
    </div>
  );
};

